import {
    BadgeCheck,
    BriefcaseBusinessIcon,
    ChevronsUpDown,
    LogOut,
    UserCircle2,
    Users,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LogoutAction from "@/api/logoutAction";
import { Link, useNavigate } from "react-router-dom";
import { RootState, persistor } from "@/store/store";
import hasAnyPermission from "@/lib/hasAnyPermission";
import { modules, permissions } from "@/lib/permissions";
import { useSelector } from "react-redux";
import { User } from "@/v2/types";

export function NavUser({ user }: { user: User | null }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: LogoutAction,
        onSuccess: () => {
            queryClient.removeQueries();
            persistor.purge();
            navigate("/login");
        },
    });

    function handleLogout() {
        mutation.mutate();
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger className="w-full rounded-md outline-none ring-ring hover:bg-accent focus-visible:ring-2 data-[state=open]:bg-accent">
                <div className="flex items-center gap-2 px-2 py-1.5 text-left text-sm transition-all">
                    <Avatar className="h-7 w-7 rounded-md border">
                        <AvatarImage
                            src={""}
                            alt={user?.full_name}
                            className="animate-in fade-in-50 zoom-in-90"
                        />
                        <AvatarFallback className="rounded-md">
                            <UserCircle2 />
                        </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 leading-none">
                        <div className="font-medium">{user?.full_name}</div>
                        <div className="overflow-hidden text-xs text-muted-foreground">
                            <div className="line-clamp-1">{user?.email}</div>
                        </div>
                    </div>
                    <ChevronsUpDown className="ml-auto mr-0.5 h-4 w-4 text-muted-foreground/50" />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-56"
                align="end"
                side="right"
                sideOffset={4}
            >
                <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm transition-all">
                        <Avatar className="h-7 w-7 rounded-md">
                            <AvatarImage src={""} alt={user?.full_name} />
                            <AvatarFallback>
                                <UserCircle2 />
                            </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1">
                            <div className="font-medium">{user?.full_name}</div>
                            <div className="overflow-hidden text-xs text-muted-foreground">
                                <div className="line-clamp-1">
                                    {user?.email}
                                </div>
                            </div>
                        </div>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                    <Link to={"account"}>
                        <DropdownMenuItem className="gap-2">
                            <BadgeCheck className="h-4 w-4 text-muted-foreground" />
                            Account
                        </DropdownMenuItem>
                    </Link>

                    {hasAnyPermission(
                        useSelector((state: RootState) => state.user.value),
                        {
                            module: modules.AR_ENGINEERING_PROFILE_MANAGEMENT,
                            scope: permissions.ALL_AR_ENGINEERING_PROFILE_MANAGEMENT,
                        },
                    ) && (
                        <Link to={"account/company-details"}>
                            <DropdownMenuItem className="gap-2">
                                <BriefcaseBusinessIcon className="h-4 w-4 text-muted-foreground" />
                                Company Details
                            </DropdownMenuItem>
                        </Link>
                    )}
                    {hasAnyPermission(
                        useSelector((state: RootState) => state.user.value),
                        {
                            module: modules.USER_MANAGEMENT,
                            scope: permissions.ALL_USER_MANAGEMENT,
                        },
                    ) && (
                        <Link to={"members"}>
                            <DropdownMenuItem className="gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                Members
                            </DropdownMenuItem>
                        </Link>
                    )}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2" onClick={handleLogout}>
                    {mutation.isPending ? (
                        <>
                            <LogOut className="h-4 w-4 text-muted-foreground" />
                            Logging out..
                        </>
                    ) : (
                        <span className="flex items-center gap-2">
                            <LogOut className="h-4 w-4 text-muted-foreground" />{" "}
                            Log Out{" "}
                        </span>
                    )}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
